import clsx from "clsx";
import { useState } from "react";
import Link from "next/link";
import { useLoyalty } from "@chef/feature-flags";
import { formatDate } from "@chef/helpers";
import { ChevronRight } from "@chef/icons/small";
import { BRAND_PATHS } from "@chef/constants";
import { Skeleton } from "@chef/components";
import { intl } from "./PointDetails.Intl";

export const PointDetails = () => {
  const { data } = useLoyalty();

  const [showAll, setShowAll] = useState(false);

  const pointsByExpiryDate = data?.loyaltyEntries.reduce((acc, entry) => {
    // We group by day, so we need to remove the time from the date
    const expirationDateWithoutTime = formatDate(
      new Date(entry.expirationDate),
      "yyyy-MM-dd",
    );

    const existingEntry = acc.get(expirationDateWithoutTime);

    if (existingEntry) {
      acc.set(expirationDateWithoutTime, existingEntry + entry.points);
    } else {
      acc.set(expirationDateWithoutTime, entry.points);
    }

    return acc;
  }, new Map<string, number>());

  const points = data?.availableLoyaltyPoints;

  const allPoints = pointsByExpiryDate && Array.from(pointsByExpiryDate);
  const firstThreePoints = allPoints && [...allPoints].slice(0, 3);

  const pointsToShow = showAll ? allPoints : firstThreePoints;

  return (
    <div className="flex flex-col">
      <Link
        href={BRAND_PATHS.LOYALTY_POINTSHOP_HREF}
        className="flex justify-between"
      >
        <div className="flex items-center gap-1">
          <strong>
            <p>{intl.POINTS_YOU_CAN_SPEND}:</p>
          </strong>

          {points === undefined ? (
            <Skeleton width={14} height={4} />
          ) : (
            <p>{points}</p>
          )}
        </div>

        <ChevronRight />
      </Link>

      {pointsByExpiryDate && pointsByExpiryDate.size === 0 ? (
        <div className="flex gap-1 mt-4">
          <p>{intl.YOU_CURRENTLY_HAVE_NO_POINTS}</p>
          <Link
            href={BRAND_PATHS.LOYALTY_HREF}
            className="underline lowercase text-primary"
          >
            {intl.READ_MORE_ABOUT_POINTS_HERE}
          </Link>
        </div>
      ) : (
        <>
          <strong>
            <p className="mt-5 text-sm">{intl.POINTS_THAT_EXPIRE}:</p>
          </strong>

          <ul className="mt-2 grow">
            {pointsToShow
              ? pointsToShow.map(([date, points], index) => (
                  <li
                    key={date}
                    className={clsx(
                      "flex justify-between w-full py-2 px-3",
                      index % 2 === 0 ? "bg-background" : "bg-white",
                    )}
                  >
                    <p>
                      {formatDate(new Date(date), intl.POINT_EXPIRY_FORMAT)}
                    </p>
                    <p>{points}</p>
                  </li>
                ))
              : new Array(3).fill(null).map((_, index) => (
                  <li key={index}>
                    <Skeleton
                      className={clsx(
                        index % 2 === 0 ? "bg-background" : "bg-white",
                      )}
                      height={4}
                    />
                  </li>
                ))}
          </ul>
        </>
      )}

      {allPoints && allPoints.length > 3 && (
        <button
          className="mt-6 font-bold underline text-primary"
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? intl.SHOW_LESS : `${intl.SHOW_ALL} (${allPoints?.length})`}
        </button>
      )}
    </div>
  );
};
